import React, { useEffect, useState } from 'react';
import { Add, Search } from '@sede-enterprise/shell-ds-react-framework/build/esm/components/Icon/components';
import { ColumnDef } from '@tanstack/react-table';
import { FormField, Select, Option, Button, TextInput, BaseTable, Modal, SingleDatePicker, Label, Toggle, SinglePickerValueType, TextTypes } from '@sede-enterprise/shell-ds-react-framework';
import dayjs from 'dayjs';
import { AnchorSpan, ContainerDiv } from '../styledComponent/StyledComponent';
import { Col, Row } from '../flexbox/Flex';
import { postData } from '../../services/ApiService';
import { ApiUrls } from '../../helpers/ApiUrls';
import { DefaultOptionType } from 'rc-select/lib/Select';
import ResponseErrorModal from '../responseErrorModal/ResponseErrorModal';
import Loader from '../loader/Loader';
import { useToast } from 'rc-toastr';
import { APIErrorResponseInterface } from '../../helpers/InterfacesConstants';
import StatusCell from '../common/StatusCell';

interface customer  {
    id?: string;
    customerName : string;
    customerCode: string;
    startDate : SinglePickerValueType | null;
    endDate : SinglePickerValueType | null;
    isActive : boolean;
    isLocal : boolean;
}


const Customers = (props: any) => {

    const [openModal, setOpenModal] = useState(false);
    const [modelTitle, setModelTitle] = useState('');
    const [searchText , setSearchText] = useState('');
    const [miCustomers, setMiCustomers] = useState<customer[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isOnLoad, setIsOnLoad] = useState(true);
    const [customerDetails, setCustomerDetails] = useState<customer>({
        id: '',
        customerName : '',
        customerCode: '',
        startDate : null,
        endDate : null,
        isActive : false,
        isLocal : false
    });
    const { toast } = useToast();
    const [customersList, setCustomersList ] = useState<{customerId: string, customerName: string, customerCodes:string[]}[]>([]);
    const [customerCodesList, setCustomerCodesList ] = useState<string[]>([]);

    const [apiErrorResponse, setApiErrorResponse] = useState<APIErrorResponseInterface | null>();
    const columns: ColumnDef<customer>[] = [{
        header: 'Customer Name',
        accessorKey: 'customerName',
        size: 60
    }, {
        header: 'Customer Code',
        accessorKey: 'customerCode',
        size: 60
    },{
        header: 'Start Date',
        accessorKey: 'startDate',
        cell: ({row}) =>{
            return(
                dayjs(row.original.startDate).format('DD MMM YYYY')
            )
        },
        size: 60
    }, {
        header: 'End Date',
        accessorKey: 'endDate',
        cell: ({row}) =>{
            return(
                dayjs(row.original.endDate).format('DD MMM YYYY')
            )
        },
        size: 60
    }, {
        header: 'Status',
        accessorKey: 'isActive',
        cell: ({row}) =>{
            const isActive = row.original.isActive;
            return(
                <StatusCell status={isActive}/>
            )
        },
        size: 60
    }, {
        header: 'Local Customer',
        accessorKey: 'isLocal',
        cell: ({row}) =>{
            const isLocal = row.original.isLocal;
            return(
                isLocal ? 'True' : 'False'
            )
        },
        size: 60
    }, {
        header: 'Action',
        accessorKey: 'id',
        size: 60,
        cell: ({row}) =>{
            return(
                <AnchorSpan onClick={()=>{handleEditClick(row.original)}} key={row.id} style={{float: 'right', justifyContent: 'end', minWidth: '35px'}}>Edit</AnchorSpan>
            )
        },
    }];


    const handleEditClick = (row: customer) => {
        setModelTitle('Edit MI Customer');
        setCustomerDetails({...row, startDate: dayjs(row.startDate), endDate: dayjs(row.endDate)});
        setOpenModal(true);
    }

    const handleAddClick = () => {
        setCustomersList(props.getCustomerList());
        setModelTitle('Add MI Customer');
        setCustomerDetails({
            customerName : '',
            customerCode: '',
            startDate : null,
            endDate : null,
            isActive : true,
            isLocal : false,
        });
        setOpenModal(true);
    }

    const save = () => {
        
        const obj = {
            ...customerDetails,
            startDate: dayjs(customerDetails.startDate).format('YYYY-MM-DD'),
            endDate: dayjs(customerDetails.endDate).format('YYYY-MM-DD')
        }
        postData(ApiUrls.addorupdatecustomer, obj).then((response) => {
            if(response.status == 200){
                getMiCustomers("");
                toast.success("Added Customer to MI List");
                setOpenModal(false);
            }
        }).catch((error) => {
            setApiErrorResponse({
                message : error.response.data.message,
                traceId : error.response.data.traceId
            });
        });

    }

    const getMiCustomers = (search: string) =>{
        setIsLoading(true);
        postData(ApiUrls.miCustomers, {"searchTerm": search}).then((response) => {
            setMiCustomers(response.data);
            setIsLoading(false);
        }).catch((error) => {
            setApiErrorResponse({
                message : error.response.data.message,
                traceId : error.response.data.traceId
            });
            setIsLoading(false);
        })
    }

    useEffect(() => {
        if(isOnLoad){
            setIsOnLoad(false);
            getMiCustomers(searchText);
        }
    },[searchText]);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            getMiCustomers(searchText);
        }
    };

    const onCustomerChange = (value: string, option: DefaultOptionType | DefaultOptionType[] ) => {

        if (Array.isArray(option)) {
            option.forEach((opt) => {
                if(opt['data-customercodes']){
                    setCustomerCodesList(opt['data-customercodes']);
                }
            });
        } else {
            if(option && option['data-customercodes']){
                setCustomerCodesList(option['data-customercodes']);
            }else{
                setCustomerCodesList([]);
            }
        }
        setCustomerDetails({...customerDetails, customerName: value, customerCode:''})
    }

    
    return (
        <>
            <ContainerDiv>
                <Row>
					<Col size='65'>
						<Button onClick={()=>{handleAddClick()}}><Add/>Add MI Customer</Button>
					</Col>
                    <Col size='35'>
                        <Row>
                            <Label bold style={{paddingRight:'4px'}} type={TextTypes.Label} htmlFor='search'>Search</Label>
                            <TextInput size="medium" onChange={(event)=>{setSearchText(event.target.value)}}  suffix={{node: <Search />}} placeholder="Search by Customer Name" id="search" onKeyDown={handleKeyDown}/>
                        </Row>
					</Col>
                </Row>
            </ContainerDiv>
            
            <Row>
                <Col size="100">
                    { !isLoading && <BaseTable data={miCustomers} columns={columns} noFooter/>}
                    { isLoading && <Loader/>}
                </Col>
            </Row>

            <Modal  open={openModal} onClose={() => {
                    setOpenModal(false);
                }} 

                actions={[{
                    label: 'Save',
                    action: () => {
                        save()
                    }
                }]}
                title={modelTitle}
                
                >
                <>
                    <FormField id="customerName" label="Customer">
                        <Select value={customerDetails.customerName} onChange={(value, option) =>{onCustomerChange(value, option)}}>
                            {customersList.map((customer)=>(                              
                                <Option value={customer.customerId.toString()} key={customer.customerId} data-customercodes={customer.customerCodes}>{customer.customerName}_{customer.customerId} </Option>
                            ))}
                        </Select>
                    </FormField>

                    <FormField id="customerCode" label="Customer Code">
                        <Select value={customerDetails.customerCode} onChange={(value) =>{setCustomerDetails({...customerDetails, customerCode: value})}}>
                            {customerCodesList.map((customerCode)=>(
                                <Option key={customerCode} value={customerCode}>{customerCode}</Option>
                            ))}
                        </Select>
                    </FormField>

                    <FormField id="startDate" label="Start Date">
                        <SingleDatePicker value={customerDetails.startDate} onChange={(value) =>{setCustomerDetails({...customerDetails, startDate: value})}} size='small' showToday={false}/>
                    </FormField>

                    <FormField id="endDate" label="End Date">
                        <SingleDatePicker value={customerDetails.endDate} onChange={(value) =>{setCustomerDetails({...customerDetails, endDate: value})}} size='small' showToday={false}/>
                    </FormField>
                    
                    <div style={{marginBottom:'10px'}}>
                    <Row>
                        <Col size="90"><Label bold className='statusLabel'>Status</Label></Col>
                        <Col size="10"><Toggle checked={customerDetails.isActive} onChange={(value)=>{setCustomerDetails({ ...customerDetails, isActive: value})}} /></Col>
                    </Row>
                    </div>

                    <Row>
                        <Col size="90"><Label bold className='statusLabel'>Local Customer</Label></Col>
                        <Col size="10"><Toggle checked={customerDetails.isLocal} onChange={(value)=>{setCustomerDetails({ ...customerDetails, isLocal: value})}} /></Col>
                    </Row>
                </>
            </Modal>
            <ResponseErrorModal errorDetails={apiErrorResponse} isOpen={apiErrorResponse ? true : false} onModalClose={()=>{setApiErrorResponse(null)}}></ResponseErrorModal>
        </>
    )
}
export default Customers;